import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle, Info, AlertCircle, ChevronDown, ChevronUp, ShieldCheck, Wrench, Activity, Zap, Gauge } from "lucide-react";
import { useState } from "react";

const severityConfig = {
  healthy: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle },
  minor: { color: "bg-sky-100 text-sky-800 border-sky-200", icon: Info },
  moderate: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle },
  severe: { color: "bg-orange-100 text-orange-800 border-orange-200", icon: AlertCircle },
  critical: { color: "bg-red-100 text-red-800 border-red-200", icon: AlertTriangle },
  unknown: { color: "bg-slate-100 text-slate-800 border-slate-200", icon: Info },
};

const gradeColors = {
  'A': 'text-emerald-600 bg-emerald-50',
  'B': 'text-sky-600 bg-sky-50',
  'C': 'text-amber-600 bg-amber-50',
  'D': 'text-orange-600 bg-orange-50',
  'F': 'text-red-600 bg-red-50',
};

// Helper to clean and parse text that might contain JSON
function cleanSummaryText(text) {
  if (!text) return '';
  
  // If it's not a string, return empty
  if (typeof text !== 'string') return '';
  
  // Check if the text starts with JSON markers
  let cleaned = text.trim();
  
  // Remove markdown code block markers
  if (cleaned.startsWith('```json')) {
    cleaned = cleaned.replace(/^```json\s*/, '').replace(/```\s*$/, '');
  } else if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```\s*/, '').replace(/```\s*$/, '');
  }
  
  // Try to parse if it looks like JSON
  if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
    try {
      const parsed = JSON.parse(cleaned);
      // If parsed successfully, extract meaningful text
      if (parsed.diagnostic_summary) return parsed.diagnostic_summary;
      if (parsed.summary) return parsed.summary;
      if (parsed.overall_assessment) return parsed.overall_assessment;
      if (parsed.assessment) return parsed.assessment;
      // If it's an object with text content, try to find it
      if (typeof parsed === 'object') {
        const textValues = Object.values(parsed).filter(v => typeof v === 'string' && v.length > 50);
        if (textValues.length > 0) return textValues[0];
      }
      return '';
    } catch (e) {
      // Not valid JSON, continue to clean
    }
  }
  
  // Remove any remaining JSON-like patterns from the text
  cleaned = cleaned
    .replace(/^\s*["']?json\s*[{[]/i, '')
    .replace(/[}\]]\s*["']?\s*$/i, '')
    .replace(/"diagnostic_summary"\s*:\s*"/i, '')
    .replace(/"summary"\s*:\s*"/i, '')
    .replace(/"overall_assessment"\s*:\s*"/i, '')
    .replace(/",\s*"[a-z_]+"\s*:/gi, '. ')
    .replace(/\\n/g, ' ')
    .replace(/\\"/g, '"')
    .replace(/"\s*$/g, '')
    .trim();
  
  return cleaned;
}

function pickBestAnalysis(aiAnalysis) {
  if (!aiAnalysis) return null;
  const ai1 = aiAnalysis.openai;
  const ai2 = aiAnalysis.claude;

  const ai1Ok = ai1?.status === "success" && ai1?.analysis;
  const ai2Ok = ai2?.status === "success" && ai2?.analysis;

  if (ai1Ok && ai2Ok) {
    const scoreA = Object.keys(ai1.analysis || {}).length;
    const scoreB = Object.keys(ai2.analysis || {}).length;
    return scoreB > scoreA ? ai2 : ai1;
  }
  if (ai2Ok) return ai2;
  if (ai1Ok) return ai1;
  return ai1 || ai2;
}

// Detect if this is the new OBD-enhanced format
function isOBDEnhancedFormat(analysis) {
  return analysis && (
    'diagnostic_summary' in analysis ||
    'obd_dtc_analysis' in analysis ||
    'sound_obd_correlation' in analysis ||
    'confirmed_issues' in analysis
  );
}

// Get severity from engine health score
function getSeverityFromScore(score) {
  if (!score) return 'unknown';
  const overall = score.overall_score || score;
  if (overall >= 80) return 'healthy';
  if (overall >= 60) return 'minor';
  if (overall >= 40) return 'moderate';
  if (overall >= 20) return 'severe';
  return 'critical';
}

export default function WisedriveAnalysis({ aiAnalysis }) {
  const [expanded, setExpanded] = useState(true);
  const best = pickBestAnalysis(aiAnalysis);

  if (!best) return null;

  const analysis = best.analysis;
  const isError = best.status === "error";
  
  // Handle new OBD-enhanced format
  const isOBDFormat = isOBDEnhancedFormat(analysis);
  
  // Determine severity based on format
  let severity = 'unknown';
  if (isOBDFormat && analysis?.engine_health_score) {
    severity = getSeverityFromScore(analysis.engine_health_score.overall_score);
  } else if (analysis?.severity) {
    severity = analysis.severity;
  }
  
  const config = severityConfig[severity] || severityConfig.unknown;
  const SeverityIcon = config.icon;

  // Get summary text based on format and clean it
  const rawSummary = isOBDFormat 
    ? analysis?.diagnostic_summary 
    : analysis?.summary;
  const summaryText = cleanSummaryText(rawSummary);

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="wisedrive-analysis-card">
      <div className="border-b border-slate-200 p-4 bg-slate-900 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-orange-400" />
          <span className="text-sm font-bold uppercase tracking-wider text-white">Wisedrive Sound AI Engine Analysis</span>
        </div>
        <Badge className={`rounded-full text-sm font-bold uppercase tracking-widest border px-3 ${isError ? 'bg-amber-100 text-amber-800 border-amber-200' : config.color}`}>
          {isError ? "Pending" : (isOBDFormat && analysis?.engine_health_score?.grade) ? `Grade ${analysis.engine_health_score.grade}` : severity}
        </Badge>
      </div>
      <CardContent className="p-6">
        {isError ? (
          <div className="text-center py-8">
            <AlertTriangle size={36} className="mx-auto text-amber-400 mb-3" />
            <p className="text-base font-medium text-slate-700">AI Analysis Temporarily Unavailable</p>
            <p className="text-sm text-slate-400 mt-1">This section will be generated when the AI service is back online.</p>
            <p className="text-sm text-slate-400 mt-1">You can use the override feature to re-analyse this report.</p>
          </div>
        ) : analysis ? (
          <div className="space-y-5">
            {/* Summary / Diagnostic Summary */}
            {summaryText && (
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-sm border border-slate-100">
                <SeverityIcon size={22} className="mt-0.5 flex-shrink-0 text-slate-500" />
                <p className="text-base text-slate-700 leading-relaxed">{summaryText}</p>
              </div>
            )}

            {/* Engine Health Score (OBD Enhanced) */}
            {isOBDFormat && analysis.engine_health_score && (
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Engine Health Score</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div className={`flex flex-col items-center p-4 rounded-sm border ${gradeColors[analysis.engine_health_score.grade] || 'bg-slate-50'}`}>
                    <span className="text-3xl font-bold">{analysis.engine_health_score.overall_score}</span>
                    <span className="text-sm text-slate-500 mt-1">Overall</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-slate-50 rounded-sm border border-slate-100">
                    <Activity size={18} className="text-slate-400 mb-1" />
                    <span className="text-lg font-bold">{analysis.engine_health_score.combustion_health || '-'}</span>
                    <span className="text-sm text-slate-500">Combustion</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-slate-50 rounded-sm border border-slate-100">
                    <Wrench size={18} className="text-slate-400 mb-1" />
                    <span className="text-lg font-bold">{analysis.engine_health_score.mechanical_health || '-'}</span>
                    <span className="text-sm text-slate-500">Mechanical</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-slate-50 rounded-sm border border-slate-100">
                    <Zap size={18} className="text-slate-400 mb-1" />
                    <span className="text-lg font-bold">{analysis.engine_health_score.electrical_health || '-'}</span>
                    <span className="text-sm text-slate-500">Electrical</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-slate-50 rounded-sm border border-slate-100">
                    <Gauge size={18} className="text-slate-400 mb-1" />
                    <span className="text-lg font-bold">{analysis.engine_health_score.emission_system_health || '-'}</span>
                    <span className="text-sm text-slate-500">Emissions</span>
                  </div>
                </div>
              </div>
            )}

            {/* OBD DTC Analysis */}
            {isOBDFormat && analysis.obd_dtc_analysis && (
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">OBD Diagnostic Codes Analysis</p>
                <div className="p-4 bg-slate-50 rounded-sm border border-slate-100 space-y-3">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-600">Total DTCs:</span>
                    <Badge className="bg-slate-200 text-slate-800">{analysis.obd_dtc_analysis.total_dtcs}</Badge>
                  </div>
                  {analysis.obd_dtc_analysis.critical_dtcs?.length > 0 && (
                    <div>
                      <span className="text-sm text-slate-600 block mb-2">Critical Codes:</span>
                      <div className="flex flex-wrap gap-2">
                        {analysis.obd_dtc_analysis.critical_dtcs.map((code, i) => (
                          <Badge key={i} className="bg-red-100 text-red-800 border-red-200 font-mono">{code}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysis.obd_dtc_analysis.root_cause_dtc && (
                    <div className="pt-2 border-t border-slate-200">
                      <span className="text-sm text-slate-600">Root Cause DTC:</span>
                      <Badge className="ml-2 bg-orange-100 text-orange-800 border-orange-200 font-mono">{analysis.obd_dtc_analysis.root_cause_dtc}</Badge>
                    </div>
                  )}
                  {analysis.obd_dtc_analysis.dtc_chain_analysis && (
                    <p className="text-sm text-slate-600 pt-2 border-t border-slate-200">{analysis.obd_dtc_analysis.dtc_chain_analysis}</p>
                  )}
                </div>
              </div>
            )}

            {/* Sound-OBD Correlation */}
            {isOBDFormat && analysis.sound_obd_correlation?.correlations_found?.length > 0 && (
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Sound & OBD Correlation</p>
                <div className="space-y-3">
                  {analysis.sound_obd_correlation.correlations_found.slice(0, expanded ? undefined : 2).map((corr, i) => (
                    <div key={i} className="p-4 bg-sky-50/50 border border-sky-100 rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-slate-800">{corr.sound_finding}</span>
                        <Badge className={`rounded-full text-sm font-bold uppercase border px-2 ${
                          corr.correlation_strength === 'strong' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          corr.correlation_strength === 'moderate' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-slate-50 text-slate-700 border-slate-200'
                        }`}>{corr.correlation_strength}</Badge>
                      </div>
                      <p className="text-sm text-slate-600 mb-2"><strong>OBD Evidence:</strong> {corr.obd_evidence}</p>
                      <p className="text-sm text-sky-700 bg-sky-100/50 p-2 rounded"><strong>Diagnosis:</strong> {corr.combined_diagnosis}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmed Issues (OBD Format) */}
            {isOBDFormat && analysis.confirmed_issues?.length > 0 && (
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Confirmed Issues</p>
                <div className="space-y-3">
                  {analysis.confirmed_issues.slice(0, expanded ? undefined : 3).map((issue, i) => (
                    <div key={i} className="p-4 bg-amber-50/50 border border-amber-100 rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-slate-800">{issue.issue_name}</span>
                        <div className="flex gap-2">
                          <Badge className={`rounded-full text-sm font-bold uppercase border px-2 ${
                            issue.confidence === 'confirmed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            issue.confidence === 'highly_likely' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                            'bg-slate-50 text-slate-700 border-slate-200'
                          }`}>{issue.confidence?.replace('_', ' ')}</Badge>
                          <Badge className={`rounded-full text-sm font-bold uppercase border px-2 ${
                            issue.severity === 'critical' ? 'bg-red-100 text-red-800 border-red-200' :
                            issue.severity === 'severe' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                            'bg-amber-100 text-amber-800 border-amber-200'
                          }`}>{issue.severity}</Badge>
                        </div>
                      </div>
                      {issue.evidence_sound && (
                        <p className="text-sm text-slate-600 mb-1"><strong>Sound Evidence:</strong> {issue.evidence_sound}</p>
                      )}
                      {issue.evidence_obd && (
                        <p className="text-sm text-slate-600 mb-1"><strong>OBD Evidence:</strong> {issue.evidence_obd}</p>
                      )}
                      {issue.repair_action && (
                        <p className="text-sm text-slate-700 mt-2 p-2 bg-slate-100 rounded"><strong>Action:</strong> {issue.repair_action}</p>
                      )}
                      <div className="flex items-center justify-between mt-3 pt-2 border-t border-amber-200">
                        {(issue.estimated_repair_cost || issue.estimated_repair_cost_inr) && (
                          <span className="text-sm font-mono font-bold text-orange-600">{issue.estimated_repair_cost || issue.estimated_repair_cost_inr}</span>
                        )}
                        {issue.repair_urgency && (
                          <Badge className={`text-sm ${
                            issue.repair_urgency === 'immediate' ? 'bg-red-100 text-red-700' :
                            issue.repair_urgency === 'soon' ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>{issue.repair_urgency}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Prioritized Recommendations (OBD Format) */}
            {isOBDFormat && analysis.prioritized_recommendations?.length > 0 && (
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Prioritized Recommendations</p>
                <ul className="space-y-2">
                  {analysis.prioritized_recommendations.map((rec, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-3 p-2 bg-slate-50 rounded">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-bold">{i + 1}</span>
                      <div className="flex-1">
                        <span>{typeof rec === 'string' ? rec : rec.recommendation || rec.action}</span>
                        {rec.estimated_cost && (
                          <span className="ml-2 text-orange-600 font-mono text-sm">{rec.estimated_cost}</span>
                        )}
                        {rec.estimated_cost_inr && !rec.estimated_cost && (
                          <span className="ml-2 text-orange-600 font-mono text-sm">{rec.estimated_cost_inr}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Legacy Format: Sound Types */}
            {!isOBDFormat && analysis.sound_types?.length > 0 && (
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Sound Types Identified</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {analysis.sound_types.slice(0, expanded ? undefined : 4).map((st, i) => (
                    <div key={i} className="p-4 bg-slate-50 rounded-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-slate-800">{st.type}</span>
                        <Badge className={`rounded-full text-sm font-bold uppercase tracking-widest border px-2 ${
                          st.severity === 'normal' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          st.severity === 'mild' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                          st.severity === 'moderate' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-red-50 text-red-700 border-red-200'
                        }`}>{st.severity}</Badge>
                      </div>
                      <p className="text-sm text-slate-600">{st.description}</p>
                      {st.frequency_range && (
                        <p className="text-sm text-slate-500 mt-1 font-mono">{st.frequency_range}</p>
                      )}
                      {st.possible_cause && (
                        <p className="text-sm text-slate-500 mt-1">
                          <span className="font-semibold">Cause:</span> {st.possible_cause}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legacy Format: Issues */}
            {!isOBDFormat && analysis.issues_detected?.length > 0 && (
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Issues Detected</p>
                <div className="space-y-3">
                  {analysis.issues_detected.slice(0, expanded ? undefined : 3).map((issue, i) => (
                    <div key={i} className="p-4 bg-amber-50/50 border border-amber-100 rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-slate-800">{issue.issue}</span>
                        <Badge className={`rounded-full text-sm font-bold uppercase tracking-widest border px-2 ${
                          issue.urgency === 'critical' ? 'bg-red-100 text-red-800 border-red-200' :
                          issue.urgency === 'high' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                          'bg-amber-100 text-amber-800 border-amber-200'
                        }`}>
                          {issue.urgency}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">{issue.description}</p>
                      {issue.estimated_repair_cost && (
                        <p className="text-sm text-orange-600 font-mono font-bold mt-2">
                          Est. Repair: {issue.estimated_repair_cost}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legacy Format: Recommendations */}
            {!isOBDFormat && analysis.recommendations?.length > 0 && (
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Recommendations</p>
                <ul className="space-y-2">
                  {analysis.recommendations.map((r, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-3">
                      <CheckCircle size={16} className="mt-0.5 text-emerald-500 flex-shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Legacy Format: Engine Health Assessment */}
            {!isOBDFormat && analysis.engine_health_assessment && (
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Component Assessment</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(analysis.engine_health_assessment).map(([key, val]) => (
                    <div key={key} className="flex flex-col items-center p-3 bg-slate-50 rounded-sm border border-slate-100">
                      <span className="text-sm text-slate-500 capitalize text-center">{key.replace(/_/g, ' ')}</span>
                      <span className={`text-sm font-bold uppercase mt-1 ${
                        val === 'good' ? 'text-emerald-600' : val === 'fair' ? 'text-amber-600' : 'text-red-600'
                      }`}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Professional Notes (OBD Format) */}
            {isOBDFormat && analysis.professional_notes && (
              <div className="p-4 bg-slate-100 rounded-sm border border-slate-200">
                <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">Professional Notes</p>
                <p className="text-sm text-slate-600 italic">{analysis.professional_notes}</p>
              </div>
            )}

            {/* Expand/Collapse */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-slate-500 hover:text-slate-700 transition-colors"
              data-testid="toggle-expand-wisedrive"
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {expanded ? "Show Less" : "Show More"}
            </button>
          </div>
        ) : (
          <p className="text-base text-slate-400 text-center py-8">No analysis data available</p>
        )}
      </CardContent>
    </Card>
  );
}
